
import { supabase } from '@/integrations/supabase/client';
import { handleServiceError } from '@/services/utils/errorHandling';
import { FamilyActivityWithUser, ActivityFilters, ActivityFeedResponse } from './types';

export const activityFeedService = {
  /**
   * Fetch family activity logs with user information
   */
  async getFamilyActivities(
    familyId: string, 
    limit: number = 50, 
    offset: number = 0,
    filters?: ActivityFilters
  ): Promise<ActivityFeedResponse> {
    try {
      if (!familyId) {
        throw new Error("Family ID is required");
      }

      let query = supabase
        .from('family_activity_logs')
        .select(`
          *,
          profiles!inner(first_name, last_name)
        `)
        .eq('family_id', familyId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Apply filters
      if (filters?.activityType && filters.activityType !== 'all') {
        query = query.eq('activity_type', filters.activityType);
      }

      if (filters?.userId && filters.userId !== 'all') {
        query = query.eq('user_id', filters.userId);
      }

      if (filters?.dateRange) {
        query = query
          .gte('created_at', filters.dateRange.start.toISOString())
          .lte('created_at', filters.dateRange.end.toISOString());
      }

      const { data, error } = await query;
      
      // Add temporary logging to debug
      console.log('Activity query result:', { data: data?.length, error });

      if (error) throw error;

      // Transform the data to include user information
      const activitiesWithUser: FamilyActivityWithUser[] = (data || []).map(activity => {
        // Safely cast metadata to our expected type
        const metadata = typeof activity.metadata === 'object' && activity.metadata !== null 
          ? activity.metadata as FamilyActivityWithUser['metadata']
          : {};

        // Safely extract user profile data
        const userProfile = activity.profiles && typeof activity.profiles === 'object' && !Array.isArray(activity.profiles)
          ? activity.profiles as { first_name?: string; last_name?: string }
          : null;

        return {
          id: activity.id,
          family_id: activity.family_id,
          user_id: activity.user_id,
          activity_type: activity.activity_type as FamilyActivityWithUser['activity_type'],
          entity_type: activity.entity_type as FamilyActivityWithUser['entity_type'],
          entity_id: activity.entity_id,
          entity_name: activity.entity_name,
          metadata,
          created_at: activity.created_at,
          user_first_name: userProfile?.first_name || 'Unknown',
          user_last_name: userProfile?.last_name || 'User'
        };
      });

      return {
        success: true,
        data: activitiesWithUser
      };
    } catch (error) {
      handleServiceError(error, "Error fetching family activities");
      return {
        success: false,
        data: [],
        message: "Unable to load family activities at this time"
      };
    }
  },

  /**
   * Get activity statistics for the family
   */
  async getActivityStats(familyId: string, days: number = 30) {
    try {
      if (!familyId) {
        throw new Error("Family ID is required");
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('family_activity_logs')
        .select('activity_type, user_id')
        .eq('family_id', familyId)
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        byType: {} as Record<string, number>,
        byUser: {} as Record<string, number>
      };

      data?.forEach(activity => {
        stats.byType[activity.activity_type] = (stats.byType[activity.activity_type] || 0) + 1;
        stats.byUser[activity.user_id] = (stats.byUser[activity.user_id] || 0) + 1;
      });

      return { success: true, data: stats };
    } catch (error) {
      handleServiceError(error, "Error fetching activity statistics");
      return { success: false, data: null };
    }
  }
};
